import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import _ from 'lodash';
import { SortOrder } from '../../utils/enums/sort-order.enum';
import { UserStatus } from '../../utils/enums/user-status.enum';

interface IComponentStatus {
  viewSubordinateDialogOpen?: boolean;
  validateSubordinateOrganizationLoading?: boolean;
  validateSubordinateOrganizationSuccess?: boolean;
  validationError?: string;
  createSubordinateLoading?: boolean;
  createSubordinateSuccess?: boolean;
  creationError?: string;
  activeStep?: string;
  getSubordinateDetailsLoading?: boolean;
  isDeleteSubordinateModalOpen?: boolean;
  deleteSubordinateLoading?: boolean;
  isEditSubordinatePermissionsModalOpen?: boolean;
  editSubordinatePermissionsLoading?: boolean;
  newSubordinatePermissions?: string[];
  error?: string;
}

export interface SubordinatePermissions {
  permissionId: string | null;
  permissionDescription: string | null;
  activeStatus: boolean | null;
  dependencies?: number[];
}
export interface ICreateSubordinate {
  userName: string;
  firstName: string;
  lastName: string;
  mobile: string;
  nationality: string;
  password: string;
  isAgreedToTermsAndCondition?: boolean;
  token: string;
}

export interface IEditSubordinatePermissions {
  userId: string;
  permissionIds: string[];
}
export interface IValidateSubordinate {
  organizationName: string;
  inviteToken: string;
}
export interface IGetSubordinateDetailsPayload {
  userId: string;
}

export interface CompanyDetails {
  companyName: string;
  country: string;
  role: string;
  website: string;
}

export interface Subordinate {
  systemId: string | null;
  userId: string | null;
  firstName: string | '';
  familyName: string | '';
  name: string | null;
  email: string;
  avatar: string | null;
  status: UserStatus | string;
  noOfUploads: number | null;
  lastLogin: string | null;
  userSince: string | null;
  nationality: string | null;
  isNccUser?: boolean | null;
  mobile: string | null;
  permission?: SubordinatePermissions[];
  companyDetails?: CompanyDetails;
}
export interface ISubordinateSlice {
  subordinateToDelete: Subordinate | null;
  componentStatus: IComponentStatus;
  subordinate: Subordinate;
  data: Subordinate[];
  total: number;
  page: number;
  limit: number;
  isLoading: boolean;
  error: string | null;
  filters: {
    search: string;
    status: string | null;
    minUploads: number | null;
    maxUploads: number | null;
    sortBy: string;
    sortOrder: SortOrder;
    page: number;
    limit: number;
  };
}

const initialState: ISubordinateSlice = {
  subordinateToDelete: null,
  componentStatus: {
    isDeleteSubordinateModalOpen: false,
    deleteSubordinateLoading: false,
  },
  subordinate: {
    systemId: null,
    userId: null,
    firstName: '',
    familyName: '',
    name: null,
    email: '',
    avatar: null,
    status: '',
    noOfUploads: null,
    lastLogin: null,
    userSince: null,
    mobile: null,
    nationality: null,
    isNccUser: null,
  },
  data: [],
  total: 0,
  page: 1,
  limit: 10,
  isLoading: false,
  error: null,
  filters: {
    search: '',
    status: null,
    minUploads: null,
    maxUploads: null,
    sortBy: 'systemId',
    sortOrder: SortOrder.ASC,
    page: 1,
    limit: 10,
  },
};

export interface IGetSubordinatesParams {
  page?: number;
  limit?: number;
  search?: string;
  status?: string;
  minUploads?: number | null;
  maxUploads?: number | null;
  sortBy?: string;
  sortOrder?: SortOrder;
}

export interface IGetSubordinatesResponse {
  data: Subordinate[];
  total: number;
  page: number;
  limit: number;
}

export const subordinateSlice = createSlice({
  name: 'subordinate',
  initialState,
  reducers: {
    setComponentStatus(
      state: ISubordinateSlice,
      action: PayloadAction<{ [key: string]: undefined | string | boolean }>,
    ) {
      state.componentStatus = { ...state.componentStatus, ...action.payload };
    },
    clearComponentStatus(state: ISubordinateSlice, action: PayloadAction<string[] | undefined>) {
      if (action.payload?.length) {
        state.componentStatus = _.omit(state.componentStatus, action.payload);
      } else {
        state.componentStatus = {};
      }
    },
    getSubordinatesDetails(_state: ISubordinateSlice, _action: PayloadAction<IGetSubordinateDetailsPayload>) {},
    setSubordinate(state: ISubordinateSlice, action: PayloadAction<Subordinate>) {
      state.subordinate = action.payload;
    },
    validateSubordinateOrganizationAsync(_state: ISubordinateSlice, _action: PayloadAction<IValidateSubordinate>) {},
    createSubordinateAsync(_state: ISubordinateSlice, _action: PayloadAction<ICreateSubordinate>) {},
    fetchSubordinatesRequest(state, action: PayloadAction<IGetSubordinatesParams | undefined>) {
      state.isLoading = true;
      state.error = null;
      if (action.payload) {
        state.filters = { ...state.filters, ...action.payload };
      }
    },
    fetchSubordinatesSuccess(
      state,
      action: PayloadAction<{
        data: Subordinate[];
        total: number;
        page: number;
        limit: number;
      }>,
    ) {
      state.isLoading = false;
      state.data = action.payload.data;
      state.total = action.payload.total;
      state.page = action.payload.page;
      state.limit = action.payload.limit;
    },
    fetchSubordinatesFailure(state, action: PayloadAction<string>) {
      state.isLoading = false;
      state.error = action.payload;
    },
    setFilters(state, action: PayloadAction<Partial<ISubordinateSlice['filters']>>) {
      state.filters = { ...state.filters, ...action.payload };
    },
    openViewSubordinateDialog(state: ISubordinateSlice) {
      state.componentStatus.viewSubordinateDialogOpen = true;
    },
    closeViewSubordinateDialog(state: ISubordinateSlice) {
      state.componentStatus.viewSubordinateDialogOpen = false;
    },
    EditSubordinatePermissionsAsync(_state: ISubordinateSlice, _action: PayloadAction<IEditSubordinatePermissions>) {},
    setSubordinateToDelete(state, action: PayloadAction<Subordinate>) {
      state.subordinateToDelete = action.payload;
    },
    openDeleteSubordinateModal(state: ISubordinateSlice) {
      state.componentStatus.isDeleteSubordinateModalOpen = true;
    },
    closeDeleteSubordinateModal(state: ISubordinateSlice) {
      state.componentStatus.isDeleteSubordinateModalOpen = false;
    },
    setNewSubordinatePermissions(state: ISubordinateSlice, action: PayloadAction<string[]>) {
      state.componentStatus.newSubordinatePermissions = action.payload;
    },
    openEditSubordinatePermissionsModal(state: ISubordinateSlice) {
      state.componentStatus.viewSubordinateDialogOpen = false;
      state.componentStatus.isEditSubordinatePermissionsModalOpen = true;
    },
    closeEditSubordinatePermissionsModal(state: ISubordinateSlice) {
      state.componentStatus.isEditSubordinatePermissionsModalOpen = false;
      state.componentStatus.viewSubordinateDialogOpen = true;
    },
    deleteSubordinateAsync(_state, _action: PayloadAction<{ userId: string }>) {},
  },
});

export const { actions: subordinateActions, reducer: subordinateReducer } = subordinateSlice;
